/**
 * Flip Analysis tab — fix-and-flip metrics.
 *   Profit = ARV − all-in costs (purchase + rehab + closing + origination
 *            + holding + selling).
 *
 * Mockup parity (find-properties.html):
 *  - Headline tiles: ARV · Estimated Profit · ROI · Profit Margin
 *  - Holding Costs (wide): Hold-months input · Loan Payment (display from
 *    Financing) · Taxes & Insurance editable · Utilities editable · Total
 *    Holding subtotal
 *  - Selling Costs (wide): dollar amount and % of ARV — two editable views of
 *    the same figure, linked through ARV · breakdown caption
 *  - Price / Sqft · Equity Spread
 */
import { ArrowLeftRight } from "lucide-react";

import { NumberInput } from "@/components/ui/number-input";
import { cn } from "@/lib/utils";
import { computeFlip, fmtMoney, fmtPct } from "@/lib/calculators";
import { resolveSellingCost } from "@/lib/property-setting";

import { InfoTip } from "../InfoTip";
import type { DealInputsState, DerivedMetrics } from "../detail-helpers";
import { MetricTile } from "./panel-primitives";

interface FlipAnalysisProps {
  state: DealInputsState;
  metrics: DerivedMetrics;
  onChange: (patch: Partial<DealInputsState>) => void;
}

export function FlipAnalysis({ state, metrics, onChange }: FlipAnalysisProps) {
  const f = state.flip;
  const holdMonths = f.hold_months ?? 6;
  const monthlyTaxes = f.taxes_monthly ?? Math.round(state.purchase * 0.0018);
  const monthlyUtilities = f.utilities_monthly ?? 100;
  // The stored dollar amount is the selling cost; the percent is its fallback
  // and its second view. See `resolveSellingCost`.
  const { value: sellValue, pct: sellPct } = resolveSellingCost(f, state.arv);

  const result = computeFlip({
    purchase: state.purchase,
    rehab: state.rehab,
    arv: state.arv,
    closingCost: metrics.closingDollar,
    hmlLoanAmt: metrics.loanAmount,
    hmlOrigFee: metrics.origFee,
    hmlMonthlyDebt: metrics.monthlyDebt,
    monthlyTaxes,
    monthlyUtilities,
    holdMonths,
    sellPct,
  });

  function patchFlip(patch: Partial<DealInputsState["flip"]>) {
    onChange({ flip: { ...state.flip, ...patch } });
  }

  // The two selling-cost boxes are one number in two units, and editing either
  // writes both (`flip_selling_cost.flip_selling_cost_percent` / `_value`).
  // Same ceiling on each — 15% of ARV — so neither can be pushed somewhere the
  // other can't follow.
  const sellValueInput = Math.round(sellValue);
  const sellMaxValue = Math.max(0, Math.round(state.arv * 0.15));
  // A dollar amount rarely divides into a round percent ($1,234 of $89,600 is
  // 1.3772413793103448%), and the raw float overflows the box. Round the
  // DISPLAY only: the stored dollars are what the deal is costed on, so the
  // amount never drifts to match a prettier percent.
  const sellPctInput = Math.round(sellPct * 100) / 100;

  function handleSellPct(pct: number) {
    patchFlip({ sell_pct: pct, sell_value: Math.round(state.arv * (pct / 100)) });
  }

  function handleSellValue(value: number) {
    patchFlip({
      sell_pct: state.arv > 0 ? (value / state.arv) * 100 : 0,
      sell_value: value,
    });
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <ArrowLeftRight className="h-4 w-4 text-primary" aria-hidden />
        Flip Analysis
        <InfoTip text="Fix-and-flip metrics — buy, rehab, sell. Profit = ARV minus all-in costs (purchase, rehab, closing, financing, holding, selling)." />
      </h3>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <MetricTile
          label="ARV"
          value={fmtMoney(state.arv)}
          sub="After-repair value"
          tone="pos"
        />
        <MetricTile
          label="Estimated Profit"
          value={
            (result.profit < 0 ? "−" : "") + fmtMoney(Math.abs(result.profit))
          }
          sub="ARV − all-in costs"
          tone={result.profit >= 0 ? "pos" : "neg"}
        />
        <MetricTile
          label="ROI"
          value={fmtPct(result.roi)}
          sub="Profit ÷ Cash invested"
          tone={result.roi >= 0 ? "pos" : "neg"}
        />
        <MetricTile
          label="Profit Margin"
          value={fmtPct(result.margin)}
          sub="Profit ÷ ARV · target ≥ 15%"
          tone={result.margin >= 15 ? "pos" : result.margin >= 0 ? "dim" : "neg"}
        />

        {/* Holding Costs — wide tile with editable breakdown */}
        <div className="rounded-md border border-border bg-muted/40 p-3 sm:col-span-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] text-muted-foreground">Holding Costs</div>
            <div className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <span>Hold</span>
              <NumberInput
                min={1}
                max={36}
                value={holdMonths}
                parse={(raw) => Math.max(1, Math.min(36, Number(raw) || 1))}
                onChange={(v) => patchFlip({ hold_months: v })}
                className="h-6 w-14 border-border bg-card px-2 py-0 text-right text-[11px] font-bold focus-visible:ring-0 focus-visible:ring-offset-0"
                aria-label="Hold months"
              />
              <span>mo</span>
            </div>
          </div>
          <div className="mt-1 text-lg font-extrabold leading-tight">
            {fmtMoney(result.holding)}
          </div>
          <div className="mt-3 flex flex-col divide-y divide-dashed divide-border/60">
            <HoldDisplayRow
              label="Loan Payment"
              sub="from Financing"
              value={-metrics.monthlyDebt}
            />
            <HoldEditableRow
              label="Taxes & Insurance"
              value={monthlyTaxes}
              onChange={(v) => patchFlip({ taxes_monthly: v })}
            />
            <HoldEditableRow
              label="Utilities"
              value={monthlyUtilities}
              onChange={(v) => patchFlip({ utilities_monthly: v })}
            />
            <div className="flex items-center justify-between border-t border-border pt-2 text-[12px] font-bold">
              <span>
                Total Holding{" "}
                <span className="text-[10px] font-medium text-muted-foreground">
                  over {holdMonths} mo
                </span>
              </span>
              <span>{fmtMoney(result.holding)}</span>
            </div>
          </div>
        </div>

        {/* Selling Costs — wide tile, editable as either dollars or % of ARV */}
        <div className="rounded-md border border-border bg-muted/40 p-3 sm:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[11px] text-muted-foreground">Selling Costs</div>
            <div className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <span>Amount</span>
                <span>$</span>
                <NumberInput
                  min={0}
                  max={sellMaxValue}
                  step={100}
                  value={sellValueInput}
                  parse={(raw) =>
                    Math.max(0, Math.min(sellMaxValue, Number(raw) || 0))
                  }
                  onChange={handleSellValue}
                  className="h-6 w-24 border-border bg-card px-2 py-0 text-right text-[11px] font-bold focus-visible:ring-0 focus-visible:ring-offset-0"
                  aria-label="Selling cost in dollars"
                />
              </span>
              <span className="inline-flex items-center gap-1">
                <span>% of ARV</span>
                <NumberInput
                  min={0}
                  max={15}
                  step={0.25}
                  value={sellPctInput}
                  parse={(raw) => Math.max(0, Math.min(15, Number(raw) || 0))}
                  onChange={handleSellPct}
                  className="h-6 w-[68px] border-border bg-card px-2 py-0 text-right text-[11px] font-bold focus-visible:ring-0 focus-visible:ring-offset-0"
                  aria-label="Selling cost percent of ARV"
                />
                <span>%</span>
              </span>
            </div>
          </div>
          <div className="mt-1 text-lg font-extrabold leading-tight">
            {fmtMoney(result.selling)}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {sellPct.toFixed(2)}% of ARV · Agent commission · transfer tax ·
            sell-side closing
          </div>
        </div>

        <MetricTile
          label="Price / Sqft"
          value={fmtMoney(metrics.pricePerSqft)}
          sub="Purchase ÷ Sqft"
        />
        <MetricTile
          label="Equity Spread"
          value={fmtMoney(result.equitySpread)}
          sub="ARV − (Purchase + Rehab)"
          tone={result.equitySpread >= 0 ? "pos" : "neg"}
        />
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Holding-cost row primitives (mirror BrrrrSnapshot)
 * ────────────────────────────────────────────────────────────────────────── */

function HoldDisplayRow({
  label,
  sub,
  value,
}: {
  label: string;
  sub?: string;
  value: number;
}) {
  const isNegative = value < 0;
  return (
    <div className="flex items-center justify-between py-2 text-[12px]">
      <span className="text-muted-foreground">
        {label}
        {sub && (
          <span className="ml-1 text-[10px] font-medium text-muted-foreground/70">
            {sub}
          </span>
        )}
      </span>
      <span
        className={cn(
          "font-semibold",
          isNegative ? "text-destructive" : "text-foreground",
        )}
      >
        {isNegative ? "−" : ""}
        {fmtMoney(Math.abs(value))}
      </span>
    </div>
  );
}

function HoldEditableRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 text-[12px]">
      <span className="text-muted-foreground">{label}</span>
      <div className="relative w-28 rounded-md border border-destructive/30 bg-card focus-within:border-destructive">
        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[12px] font-semibold text-destructive">
          -$
        </span>
        <NumberInput
          min={0}
          value={value}
          parse={(raw) => Math.max(0, Number(raw) || 0)}
          onChange={onChange}
          className="h-7 w-full border-0 bg-transparent pl-8 pr-2 text-right text-[12px] font-bold text-destructive focus-visible:ring-0 focus-visible:ring-offset-0"
          aria-label={label}
        />
      </div>
    </div>
  );
}
